import { formatMoney } from "@sylviq/ui-core";
// Banking hub: the business's bank accounts, low-balance warnings, and
// entry points to account reconciliation and bank rules.
//
// Reads the account list, low-balance alerts, and per-account match actions.
// There is no single-account read, so the detail page rebuilds its account
// row from this list. Imported lines are listed on that detail page.
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  applyBankRules,
  autoReconcile,
  BANK_ACCOUNT_TYPES,
  type BankAccount,
  bankAccountIsActive,
  bankAccountTypeLabel,
  createBankAccount,
  getLowBalanceAlerts,
  type LowBalanceAlert,
  listBankAccounts,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";
import {
  APPLY_RULES_LABEL,
  APPLY_RULES_RUNNING_LABEL,
  APPLY_RULES_TITLE,
  AUTO_MATCH_CONFIDENCE_THRESHOLD,
  AUTO_MATCH_LABEL,
  AUTO_MATCH_RUNNING_LABEL,
  AUTO_MATCH_TITLE,
} from "./match-copy";

const DISPLAY_CURRENCY = "USD";

type MatchAction = "auto" | "rules";

interface RunTarget {
  account: BankAccount;
  action: MatchAction;
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function activeTone(account: BankAccount): StatusTone {
  return bankAccountIsActive(account) ? "success" : "idle";
}

function AccountActions({
  account,
  running,
  onRun,
}: {
  account: BankAccount;
  running: string | null;
  onRun: (account: BankAccount, action: MatchAction) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button asChild variant="secondary" size="sm">
        <Link
          to="/banking/$accountId"
          params={{ accountId: account.id }}
        >
          Reconcile
        </Link>
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={running !== null}
        onClick={() => onRun(account, "auto")}
        title={AUTO_MATCH_TITLE}
      >
        {running === `auto:${account.id}` ? AUTO_MATCH_RUNNING_LABEL : AUTO_MATCH_LABEL}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={running !== null}
        onClick={() => onRun(account, "rules")}
        title={APPLY_RULES_TITLE}
      >
        {running === `rules:${account.id}` ? APPLY_RULES_RUNNING_LABEL : APPLY_RULES_LABEL}
      </Button>
    </div>
  );
}

export function BankingPage() {
  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [alerts, setAlerts] = useState<LowBalanceAlert[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("checking");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [runTarget, setRunTarget] = useState<RunTarget | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([listBankAccounts(), getLowBalanceAlerts()])
      .then(([rows, low]) => {
        setAccounts(rows);
        setAlerts(low.alerts ?? []);
      })
      .catch((cause: unknown) => setLoadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const openDrawer = () => {
    setCreateError(null);
    setDrawerOpen(true);
  };

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setCreateError("Give the account a name first.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    setError(null);
    setNotice(null);
    try {
      await createBankAccount({
        account_name: trimmed,
        account_type: accountType,
      });
      setName("");
      setDrawerOpen(false);
      setNotice(`Opened ${trimmed}.`);
      reload();
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const openRun = (account: BankAccount, action: MatchAction) => {
    setError(null);
    setNotice(null);
    setRunTarget({ account, action });
  };

  const confirmRun = async () => {
    const target = runTarget;
    if (!target) return;
    const { account, action } = target;
    setRunning(`${action}:${account.id}`);
    setError(null);
    setNotice(null);
    try {
      if (action === "auto") {
        const result = await autoReconcile(account.id, {
          confidence_threshold: AUTO_MATCH_CONFIDENCE_THRESHOLD,
        });
        const checked = result?.checked ?? 0;
        const matched = result?.reconciled ?? 0;
        const skipped = result?.skipped ?? 0;
        setNotice(
          `${account.account_name}: checked ${checked}, matched ${matched}, skipped ${skipped}.`,
        );
      } else {
        const result = await applyBankRules(account.id);
        const applied = result?.applied ?? 0;
        const matched = result?.reconciled ?? 0;
        setNotice(
          `${account.account_name}: applied ${applied} rule${applied === 1 ? "" : "s"}, matched ${matched}.`,
        );
      }
      setRunTarget(null);
    } catch (cause) {
      setRunTarget(null);
      setError(toErrorMessage(cause));
    } finally {
      setRunning(null);
    }
  };

  const alertFor = (id: string): LowBalanceAlert | undefined =>
    (alerts ?? []).find((alert) => alert.bank_account_id === id);

  const dialogTitle = runTarget
    ? runTarget.action === "auto"
      ? `Auto-match “${runTarget.account.account_name}”?`
      : `Apply rules to “${runTarget.account.account_name}”?`
    : "";
  const dialogDescription = runTarget
    ? runTarget.action === "auto"
      ? `Auto-match compares each unmatched imported line on this account with existing ledger and journal entries and matches the ones scoring at or above the tested ${AUTO_MATCH_CONFIDENCE_THRESHOLD}% threshold. It records the matches in the books; it never moves money.`
      : "Apply rules runs your saved matching rules over this account's imported lines and records the matches in the books. It never moves money."
    : "";

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Bank accounts"
        description="Every bank account for this organization and its balance. Open an account to import statements and match its lines against existing ledger entries. Matching records results in the books; it never moves money."
        actions={
          <>
            <Button type="button" onClick={openDrawer}>
              New bank account
            </Button>
            <Button asChild variant="secondary">
              <Link to="/banking/rules">Bank rules</Link>
            </Button>
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      {(alerts ?? []).length > 0 ? (
        <section
          aria-label="Low balance warnings"
          className="surface rounded-2xl border border-[var(--status-warning-foreground)] p-5"
        >
          <h2 className="text-sm font-semibold">Running low</h2>
          <ul className="mt-2 grid gap-1 text-sm">
            {(alerts ?? []).map((alert) => (
              <li key={alert.bank_account_id}>
                <Link
                  to="/banking/$accountId"
                  params={{ accountId: alert.bank_account_id }}
                  className="font-medium underline"
                >
                  {alert.account_name}
                </Link>{" "}
                holds {money(alert.balance_cents)} — below the{" "}
                {money(alert.threshold_cents)} warning level.
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-label="Bank accounts" className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight">Accounts</h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={reload}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        {loading && accounts === null ? (
          <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
            Loading…
          </div>
        ) : loadError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {loadError}
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={reload}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : (accounts ?? []).length === 0 ? (
          <EmptyState
            title="No bank accounts yet"
            reason="Open your first account to import statements and match its lines against existing ledger entries."
          />
        ) : (
          <>
            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="banking-accounts-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(accounts ?? []).map((account) => {
                      const alert = alertFor(account.id);
                      return (
                        <TableRow key={account.id}>
                          <TableCell>
                            <Link
                              to="/banking/$accountId"
                              params={{ accountId: account.id }}
                              className="font-medium underline"
                            >
                              {account.account_name}
                            </Link>
                            {alert ? (
                              <span className="ml-2 text-xs font-medium text-[var(--status-warning-foreground)]">
                                Below {money(alert.threshold_cents)}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {humanizeLabel(
                              bankAccountTypeLabel(account.account_type),
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(account.balance_cents)}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={activeTone(account)}
                              label={
                                bankAccountIsActive(account)
                                  ? "Open"
                                  : "Closed"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap justify-end gap-2">
                              <AccountActions
                                account={account}
                                running={running}
                                onRun={openRun}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="banking-accounts-stack"
            >
              {(accounts ?? []).map((account) => {
                const alert = alertFor(account.id);
                return (
                  <article
                    key={account.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/banking/$accountId"
                        params={{ accountId: account.id }}
                        className="record-title font-medium underline"
                      >
                        {account.account_name}
                      </Link>
                      <span className="font-medium tabular-nums">
                        {money(account.balance_cents)}
                      </span>
                    </div>
                    {alert ? (
                      <p className="mt-1 text-xs font-medium text-[var(--status-warning-foreground)]">
                        Below {money(alert.threshold_cents)}
                      </p>
                    ) : null}
                    <CardField label="Type">
                      {humanizeLabel(
                        bankAccountTypeLabel(account.account_type),
                      )}
                    </CardField>
                    <CardField label="Balance">
                      {money(account.balance_cents)}
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={activeTone(account)}
                        label={bankAccountIsActive(account) ? "Open" : "Closed"}
                      />
                    </CardField>
                    <div className="pt-3">
                      <AccountActions
                        account={account}
                        running={running}
                        onRun={openRun}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      <Drawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) setCreateError(null);
        }}
        title="New bank account"
        description="Register a bank account for this organization. Importing statements and matching lines become available once the account exists."
        className="w-[min(34rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawerOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="ba-create-form"
              disabled={creating || !name.trim()}
              className="ms-auto"
            >
              {creating ? "Opening…" : "Open account"}
            </Button>
          </>
        }
      >
        <form
          id="ba-create-form"
          onSubmit={submitCreate}
          className="grid gap-4"
          aria-label="Open a bank account"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label htmlFor="ba-name" className="grid gap-1 text-xs font-medium">
            Account name
            <Input
              id="ba-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Business checking"
              disabled={creating}
            />
          </label>
          <label htmlFor="ba-type" className="grid gap-1 text-xs font-medium">
            Type
            <select
              id="ba-type"
              className="select"
              value={accountType}
              onChange={(event) => setAccountType(event.target.value)}
              disabled={creating}
            >
              {BANK_ACCOUNT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {bankAccountTypeLabel(type)}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Drawer>

      <Dialog
        open={runTarget !== null}
        onOpenChange={(open) => {
          if (!open && running === null) setRunTarget(null);
        }}
        title={dialogTitle}
        description={dialogDescription}
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRunTarget(null)}
            disabled={running !== null}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void confirmRun()}
            disabled={running !== null}
          >
            {running !== null
              ? runTarget?.action === "auto"
                ? "Matching…"
                : "Applying…"
              : runTarget?.action === "auto"
                ? "Run auto-match"
                : "Run rules"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
